// WebSocket Service for real-time updates from AWS API Gateway
import { fetchAuthSession } from 'aws-amplify/auth';

// Simple EventEmitter implementation for browser
class EventEmitter {
  private events: { [key: string]: Array<(...args: any[]) => void> } = {};

  on(event: string, listener: (...args: any[]) => void): void {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(listener);
  }

  off(event: string, listener: (...args: any[]) => void): void {
    if (!this.events[event]) return;
    this.events[event] = this.events[event].filter(l => l !== listener);
  }

  emit(event: string, ...args: any[]): void {
    if (!this.events[event]) return;
    this.events[event].forEach(listener => listener(...args));
  }

  removeAllListeners(event?: string): void {
    if (event) {
      delete this.events[event];
    } else {
      this.events = {};
    }
  }
}

export interface WebSocketMessage {
  action: string;
  type: 'agent-update' | 'collection-progress' | 'agent-complete' | 'error' | 'stats-update' | 'notification' | 'jv-submission-update' | 'order-created' | 'order-updated' | 'tax-request-update' | 'user-update' | 'property-update';
  sessionId?: string;
  agentType?: string;
  data: any;
  timestamp: string;
}

export interface AgentUpdate {
  sessionId: string;
  county: string;
  state?: string;
  dataType: string;
  agent: string;
  status: 'pending' | 'active' | 'completed' | 'failed';
  details?: string;
  duration?: string;
  progress?: number;
  startTime?: string;
  agentOpsUrl?: string;
  llmCost?: {
    model: string;
    tokens: number;
    cost: number;
  };
  kbEntry?: any;
  gitEntry?: any;
  collectionEntry?: any;
}

class WebSocketService extends EventEmitter {
  private ws: WebSocket | null = null;
  private reconnectInterval: number = 5000;
  private maxReconnectAttempts: number = 5;
  private reconnectAttempts: number = 0;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private wsUrl: string;
  private isConnecting: boolean = false;

  constructor() {
    super();
    // AWS API Gateway WebSocket URL - would come from environment config
    this.wsUrl = import.meta.env.VITE_WEBSOCKET_URL || 'wss://your-api-id.execute-api.us-east-1.amazonaws.com/production';
  }

  async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return;
    }

    this.isConnecting = true;

    try {
      // Get Cognito access token for authentication (required by get_user API)
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken?.toString();

      if (!accessToken) {
        console.error('No authentication token available');
        this.isConnecting = false;
        this.emit('error', new Error('Authentication required'));
        return;
      }

      // Append token to WebSocket URL as query parameter
      const wsUrlWithAuth = `${this.wsUrl}?token=${accessToken}`;

      console.log('Connecting to WebSocket with authentication');
      this.ws = new WebSocket(wsUrlWithAuth);
      this.setupEventHandlers();
    } catch (error) {
      console.error('WebSocket connection error:', error);
      this.isConnecting = false;
      this.scheduleReconnect();
    }
  }

  private setupEventHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      console.log('WebSocket connected');
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.emit('connected');
      
      // Send connection message to API Gateway
      this.send({
        action: 'connect',
        type: 'agent-update',
        data: {
          clientType: 'frontend',
          timestamp: new Date().toISOString()
        },
        timestamp: new Date().toISOString()
      });

      // Start heartbeat
      this.startHeartbeat();
    };

    this.ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        this.handleMessage(message);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      this.emit('error', error);
    };

    this.ws.onclose = (event) => {
      console.log('WebSocket disconnected:', event.code, event.reason);
      this.isConnecting = false;
      this.stopHeartbeat();
      this.emit('disconnected');
      
      if (event.code !== 1000) { // 1000 = normal closure
        this.scheduleReconnect();
      }
    };
  }

  private handleMessage(message: WebSocketMessage): void {
    console.log('Received message:', message.type, message);

    switch (message.type) {
      case 'agent-update':
        // Message fields are at root level, not nested under 'data'
        const { type, ...agentUpdate } = message;
        this.emit('agentUpdate', agentUpdate as AgentUpdate);
        break;
      
      case 'collection-progress':
        this.emit('collectionProgress', {
          sessionId: message.sessionId,
          progress: message.data.progress,
          recordsCollected: message.data.recordsCollected,
          errors: message.data.errors,
          estimatedTimeRemaining: message.data.estimatedTimeRemaining
        });
        break;
      
      case 'agent-complete':
        this.emit('agentComplete', {
          sessionId: message.sessionId,
          agent: message.agentType,
          result: message.data
        });
        break;
      
      case 'stats-update':
        this.emit('statsUpdate', message.data);
        break;

      case 'notification':
        this.emit('notification', message.data);
        break;

      case 'jv-submission-update':
        this.emit('jvSubmissionUpdate', message);
        break;

      case 'order-created':
      case 'order-updated':
        this.emit('orderUpdate', message);
        break;

      case 'tax-request-update':
        this.emit('taxRequestUpdate', message);
        break;

      case 'user-update':
        this.emit('userUpdate', message);
        break;

      case 'property-update':
        this.emit('propertyUpdate', message);
        break;

      case 'error':
        this.emit('agentError', {
          sessionId: message.sessionId,
          agent: message.agentType,
          error: message.data
        });
        break;

      default:
        console.warn('Unknown message type:', message.type);
    }
  }

  send(message: WebSocketMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not connected, queuing message');
      // Could implement a message queue here
    }
  }

  // Subscribe to specific agent sessions
  subscribeToSession(sessionId: string): void {
    this.send({
      action: 'subscribe',
      type: 'agent-update',
      data: { sessionId },
      timestamp: new Date().toISOString()
    });
  }

  unsubscribeFromSession(sessionId: string): void {
    this.send({
      action: 'unsubscribe',
      type: 'agent-update',
      data: { sessionId },
      timestamp: new Date().toISOString()
    });
  }

  // Request current state for all active sessions
  requestCurrentState(): void {
    this.send({
      action: 'getCurrentState',
      type: 'agent-update',
      data: {},
      timestamp: new Date().toISOString()
    });
  }

  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.send({
          action: 'ping',
          type: 'agent-update',
          data: {},
          timestamp: new Date().toISOString()
        });
      }
    }, 30000); // Send heartbeat every 30 seconds
  }

  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      this.emit('maxReconnectAttemptsReached');
      return;
    }

    this.reconnectAttempts++;
    console.log(`Scheduling reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
    
    setTimeout(() => {
      this.connect();
    }, this.reconnectInterval);
  }

  disconnect(): void {
    this.stopHeartbeat();
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
  }

  getConnectionState(): 'connecting' | 'connected' | 'disconnected' {
    if (this.isConnecting) return 'connecting';
    if (this.ws?.readyState === WebSocket.OPEN) return 'connected';
    return 'disconnected';
  }
}

// Singleton instance
export const wsService = new WebSocketService();